import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea, Toggle, Modal, Confirm, Empty } from "@/pages/admin/managers/ui";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => setItems((await api.get("/admin/testimonials")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.client_name?.trim() || !modal.comment?.trim()) return toast.error("Nombre y comentario son obligatorios.");
    try {
      const body = {
        client_name: modal.client_name, session_type: modal.session_type || "", rating: Number(modal.rating) || 5,
        comment: modal.comment, image_url: modal.image_url || "", is_example: !!modal.is_example, is_visible: modal.is_visible !== false,
      };
      if (modal.id) await api.put(`/admin/testimonials/${modal.id}`, body);
      else await api.post("/admin/testimonials", body);
      toast.success("Testimonio guardado."); setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => { await api.delete(`/admin/testimonials/${confirmId}`); toast.success("Testimonio eliminado."); setConfirmId(null); load(); };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Btn data-testid="add-testimonial-btn" onClick={() => setModal({ is_visible: true, rating: 5, is_example: false })}><Plus size={16} className="inline mr-1" /> Nuevo testimonio</Btn>
      </div>
      {items.length === 0 ? <Empty>No hay testimonios.</Empty> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
              <div className="flex gap-1 mb-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} className={i < t.rating ? "fill-[#C5A059] text-[#C5A059]" : "text-[#3f3f46]"} />)}</div>
              <p className="text-sm text-[#E4E4E7] italic">"{t.comment}"</p>
              <p className="text-white text-sm mt-3 font-medium">{t.client_name} {!t.is_visible && <span className="text-xs text-[#8C8375]">(oculto)</span>}</p>
              <p className="text-xs text-[#C5A059]">{t.session_type}</p>
              <div className="flex gap-2 mt-3">
                <Btn variant="subtle" data-testid={`testimonial-edit-${t.id}`} onClick={() => setModal(t)}><Pencil size={14} /></Btn>
                <Btn variant="danger" data-testid={`testimonial-delete-${t.id}`} onClick={() => setConfirmId(t.id)}><Trash2 size={14} /></Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.id ? "Editar testimonio" : "Nuevo testimonio"} testid="testimonial-modal">
        {modal && (
          <>
            <Field label="Nombre del cliente"><Input data-testid="testimonial-name-input" value={modal.client_name || ""} onChange={(e) => setModal({ ...modal, client_name: e.target.value })} /></Field>
            <Field label="Tipo de sesión"><Input value={modal.session_type || ""} onChange={(e) => setModal({ ...modal, session_type: e.target.value })} /></Field>
            <Field label="Calificación (1-5)"><Input type="number" min="1" max="5" value={modal.rating || 5} onChange={(e) => setModal({ ...modal, rating: e.target.value })} /></Field>
            <Field label="Comentario"><Textarea rows={3} value={modal.comment || ""} onChange={(e) => setModal({ ...modal, comment: e.target.value })} /></Field>
            <Field label="URL de foto (opcional)"><Input value={modal.image_url || ""} onChange={(e) => setModal({ ...modal, image_url: e.target.value })} /></Field>
            <Toggle checked={modal.is_visible !== false} onChange={(v) => setModal({ ...modal, is_visible: v })} label="Visible en el sitio" />
            <div className="flex justify-end gap-3 pt-2">
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
              <Btn data-testid="testimonial-save-btn" onClick={save}>Guardar</Btn>
            </div>
          </>
        )}
      </Modal>
      <Confirm open={!!confirmId} onClose={() => setConfirmId(null)} onConfirm={del} message="¿Eliminar este testimonio?" />
    </div>
  );
}
